import { Injectable, signal } from '@angular/core';
import { OriginalFormValues } from './parking-request-form-state.service';

/**
 * Interface for form field setters used during reset operations
 */
export interface FormFieldSetters {
  setBookingValue: (value: string | null) => void;
  setStartDateValue: (value: Date | null) => void;
  setEndDateValue: (value: Date | null) => void;
  setParkingPlaceValue: (value: string | null) => void;
  setDisabledParkingPlaceValue: (value: boolean) => void;
  setPaidValue: (value: boolean) => void;
  setLicensePlateNotKnownValue: (value: boolean) => void;
  setLicensePlateValue: (value: string) => void;
  setLicensePlateCountryCodeValue: (value: string | null) => void;
}

/**
 * Interface for initial values from component inputs
 */
export interface InitialFormValues {
  initialBookingId: string | null;
  initialParkingPlaceId: string | null;
  initialStartDate: Date | null;
}

/**
 * Service to handle form reset and initialization logic for parking request forms
 */
@Injectable()
export class ParkingRequestFormResetService {
  /**
   * Reset form to empty state (create mode)
   */
  resetToEmptyForm(setters: FormFieldSetters, hasChangesSignal: any): void {
    setters.setBookingValue(null);
    setters.setStartDateValue(null);
    setters.setEndDateValue(null);
    setters.setParkingPlaceValue(null);
    setters.setDisabledParkingPlaceValue(false);
    setters.setPaidValue(false);
    setters.setLicensePlateNotKnownValue(false);
    setters.setLicensePlateValue('');
    setters.setLicensePlateCountryCodeValue(null);

    // Reset change detection
    hasChangesSignal.set(false);
  }

  /**
   * Restore form values to their original state (edit mode)
   */
  restoreOriginalValues(originalValues: OriginalFormValues, setters: FormFieldSetters): void {
    if (!originalValues) return;

    setters.setBookingValue(originalValues.bookingValue);
    setters.setStartDateValue(originalValues.startDateValue ? new Date(originalValues.startDateValue) : null);
    setters.setEndDateValue(originalValues.endDateValue ? new Date(originalValues.endDateValue) : null);
    setters.setParkingPlaceValue(originalValues.parkingPlaceValue);
    setters.setDisabledParkingPlaceValue(originalValues.disabledParkingPlaceValue);
    setters.setPaidValue(originalValues.paidValue);
    setters.setLicensePlateNotKnownValue(originalValues.licensePlateNotKnownValue);
    setters.setLicensePlateValue(originalValues.licensePlateValue);
    setters.setLicensePlateCountryCodeValue(originalValues.licensePlateCountryCodeValue);

    // Reset change detection is handled by the form state service
  }

  /**
   * Apply initial values from inputs (for create mode)
   */
  applyInitialValues(initialValues: InitialFormValues, setters: FormFieldSetters, setParkingPlaceCallback?: (id: string) => void): void {
    // Set initial booking if provided
    if (initialValues.initialBookingId) {
      setters.setBookingValue(initialValues.initialBookingId);
    }

    // Set initial start date if provided
    if (initialValues.initialStartDate) {
      setters.setStartDateValue(new Date(initialValues.initialStartDate));
    }

    // Set initial parking place if provided
    if (initialValues.initialParkingPlaceId) {
      if (setParkingPlaceCallback) {
        setParkingPlaceCallback(initialValues.initialParkingPlaceId);
      } else {
        setters.setParkingPlaceValue(initialValues.initialParkingPlaceId);
      }
    }
  }

  /**
   * Perform complete form reset based on mode
   */
  performReset(
    editMode: boolean,
    originalValues: OriginalFormValues | null,
    initialValues: InitialFormValues,
    setters: FormFieldSetters,
    hasChangesSignal: any,
    setParkingPlaceCallback?: (id: string) => void
  ): void {
    if (editMode && originalValues) {
      // Edit mode: restore original values
      this.restoreOriginalValues(originalValues, setters);
    } else {
      // Create mode: reset to empty form
      this.resetToEmptyForm(setters, hasChangesSignal);

      // Apply initial values if provided
      this.applyInitialValues(initialValues, setters, setParkingPlaceCallback);
    }
  }
}
